import L from "leaflet";
import "leaflet/dist/leaflet.css";

import { addBasemap } from "@/lib/basemap";
import { useEffect, useRef, useState } from "react";

import type { AssetDataVal, AssetPosition } from "@/api/client";
import { ago } from "@/lib/format";

/** Where the asset is, how good the fix is, and how old the answer is.
 *
 *  A REAL BASEMAP, not a drawn one. The mock showed a stylised map with
 *  invented streets, which is fine in a mock and dishonest in the product: a
 *  drawn road is a road that is not there. OSM tiles, as `RouteReplay` and
 *  `FleetMap` already use -- no key, no billing, attribution the only
 *  obligation (D-109).
 *
 *  THE WORDING IS NOT MINE TO RESTYLE. "Live location", "Last known
 *  location", "no fix right now / where it was N ago" and "No GPS fix has
 *  EVER been reported" were each chosen after the card told an operator that
 *  a parked bike had no location at all, and `Position.test.tsx` asserts every
 *  one because they are the three BRANCHES a text test cannot tell apart.
 *  This redesign changes the frame; the sentences stay.
 *
 *  CONFIDENCE IS DERIVED, not restated. Dattatray asked for "confidence of
 *  GPS location (derived from HDOP)", so the raw figure is supporting detail
 *  and the band is the headline. The bands are the standard DOP ones: under 1
 *  ideal, 1-2 excellent, 2-5 good, 5-10 moderate, over 10 poor.
 */

type Vals = Record<string, AssetDataVal>;
const num = (v: Vals, k: string): number | null =>
  typeof v[k] === "number" ? (v[k] as number) : null;

const FIX_LABEL: Record<number, string> = { 0: "no fix", 2: "2D fix", 3: "3D fix" };

/** HDOP to a band. 5 is best so the meter fills to the right. */
function confidence(hdop: number | null): { word: string; lit: number; tone: string } | null {
  if (hdop == null) return null;
  if (hdop < 1) return { word: "Ideal", lit: 5, tone: "var(--ok)" };
  if (hdop <= 2) return { word: "Excellent", lit: 4, tone: "var(--ok)" };
  if (hdop <= 5) return { word: "Good", lit: 3, tone: "var(--ok)" };
  if (hdop <= 10) return { word: "Moderate", lit: 2, tone: "var(--warn)" };
  return { word: "Poor", lit: 1, tone: "var(--bad)" };
}

function MiniMap({ lat, lng, live }: { lat: number; lng: number; live: boolean }) {
  const el = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const mark = useRef<L.LayerGroup | null>(null);
  // THE MAP IS THE DECORATION, THE COORDINATE IS THE DATA. If Leaflet cannot
  // start -- no canvas, blocked tiles, a container with no size -- the card
  // must still show where the asset is. It used to throw and take the whole
  // dashboard with it.
  const [dead, setDead] = useState(false);

  useEffect(() => {
    if (!el.current || map.current) return;
    try {
    // NO `preferCanvas` HERE, unlike FleetMap. That option routes
    // `circleMarker` through the Canvas renderer, which is the right trade
    // when the map draws hundreds of dots; this one draws two, and the canvas
    // path costs a `getContext` that does not exist outside a real browser --
    // it took the whole card down under jsdom, which is also what it would do
    // in any environment without canvas.
    const m = L.map(el.current, {
      zoomControl: false, attributionControl: true,
      // a single point: dragging it away from the asset is all a user could do
      dragging: false, scrollWheelZoom: false, doubleClickZoom: false,
      boxZoom: false, keyboard: false, touchZoom: false,
    });
    addBasemap(m);
      mark.current = L.layerGroup().addTo(m);
      map.current = m;
      // THE CLEANUP RUNS LATER, outside the try above, so it needs its own.
      // An unmount that throws is never acceptable: React cannot finish
      // tearing the tree down, and the next render lands on a half-removed
      // map. Leaflet reaches into a renderer that may already be gone.
      return () => {
        try { m.remove(); } catch { /* already torn down */ }
        map.current = null;
        mark.current = null;
      };
    } catch {
      setDead(true);
      return undefined;
    }
  }, []);

  useEffect(() => {
    const m = map.current, g = mark.current;
    if (!m || !g) return;
    try {
    m.setView([lat, lng], 16);
    g.clearLayers();
    const col = live ? "var(--teal)" : "var(--warn)";
    // the halo is a FIXED radius, not an accuracy figure: the record carries
    // HDOP, which is a dilution factor and not metres, and drawing a circle
    // from it would invent a precision the device never reported
    L.circleMarker([lat, lng], { radius: 18, color: col, weight: 1,
      opacity: .35, fillColor: col, fillOpacity: .12, interactive: false }).addTo(g);
    L.circleMarker([lat, lng], { radius: 6, color: col, weight: 2,
      fillColor: col, fillOpacity: 1, interactive: false }).addTo(g);
    // Leaflet measures its container on create; in a grid cell that is often
    // still 0 wide at that moment, which leaves the tiles in one corner.
      requestAnimationFrame(() => m.invalidateSize());
    } catch { setDead(true); }
  }, [lat, lng, live]);

  if (dead) {
    return (
      <div className="lw-map lw-map-dead">
        <span>Map unavailable. The position is below.</span>
      </div>
    );
  }
  return <div ref={el} className="lw-map" />;
}

export function PositionCard({ v, pos }: { v: Vals; pos: AssetPosition | null }) {
  const has = !!(pos?.ever && pos.lat != null && pos.lng != null);
  const live = !!(has && pos!.fix);
  const hdop = num(v, "gps_hdop_d");
  const conf = confidence(hdop == null ? null : hdop / 10);
  const fix = num(v, "gps_fix");
  const alt = num(v, "gps_alt_dm");
  const head = num(v, "gps_heading_deg");
  const sat = num(v, "gps_sat");
  const dash = (x: unknown) => (x == null ? "—" : String(x));

  return (
    <section className="panel lw-pos">
      <div className="lw-vc-top">
        <span className="lw-vc-lbl">
          {live ? "Live location" : has ? "Last known location" : "Location"}
        </span>
      </div>

      {has ? (
        <>
          <div className="lw-posmap">
            <MiniMap lat={pos!.lat!} lng={pos!.lng!} live={live} />
            <a className="lw-maplink"
              href={`https://www.google.com/maps/search/?api=1&query=${pos!.lat},${pos!.lng}`}
              target="_blank" rel="noopener noreferrer"
              title="Open this position in Google Maps">Open in Maps</a>
          </div>

          <div className="lw-vc-sec">
            <div className="lw-coord">{pos!.lat!.toFixed(5)}, {pos!.lng!.toFixed(5)}</div>
            {/* A COORDINATE WITH NO AGE READS AS "now", which is the whole
                failure this card had. Not having a fix IS a status, so that
                one keeps its status colour. */}
            {live
              ? <div className="lw-posage">fix as of {ago(pos!.at)} ago</div>
              : <div className="lw-posstale">
                  no fix right now &middot; where it was {ago(pos!.at)} ago
                </div>}

            {conf && (
              <div className="lw-conf">
                <div className="lw-confk">Position confidence</div>
                <div className="lw-confv" style={{ color: conf.tone }}>{conf.word}</div>
                <div className="lw-confr">
                  Derived from HDOP {(hdop! / 10).toFixed(1)}
                  {sat != null ? ` · ${sat} satellites` : ""}
                </div>
                <div className="lw-meter">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <i key={i} className={i <= conf.lit ? "on" : undefined}
                      style={i <= conf.lit ? { background: conf.tone } : undefined} />
                  ))}
                </div>
              </div>
            )}

            <div className="lw-fixrows">
              <div className="kv"><span>Fix type</span><b>{FIX_LABEL[fix ?? -1] ?? "—"}</b></div>
              <div className="kv"><span>Satellites</span><b>{dash(sat)}</b></div>
              <div className="kv"><span>Altitude</span>
                <b>{alt == null ? "—" : `${(alt / 10).toFixed(1)} m`}</b></div>
              <div className="kv"><span>Heading</span>
                <b>{head == null ? "—" : `${head}°`}</b></div>
              {/* gps_fix_ts is the device's own fix clock and is frequently
                  NULL on this firmware, so it is a dash rather than absent:
                  the row being there is how you learn it is missing. */}
              <div className="kv"><span>Time since fix</span>
                {/* THE LONE EM-DASH IS THE MARKER, and the only allowed use
                    of one in rendered text (Chip.tsx states the rule). The
                    words go in the hover, where they are not prose on screen. */}
                <b className={pos!.at ? undefined : "lw-nil"}
                  title={pos!.at ? undefined
                    : "gps_fix_ts is null: this firmware does not report its own fix clock"}>
                  {pos!.at ? `${ago(pos!.at)} ago` : "—"}
                </b></div>
            </div>
          </div>
        </>
      ) : (
        <div className="lw-vc-sec cap">
          No GPS fix has EVER been reported for this device, so there is no
          last known position to show.
        </div>
      )}
    </section>
  );
}
